import { useEffect, useId, useRef, useState } from 'react';
import { pluriel } from './formatConfig';

const CRITERES = {
  attendu: {
    Fragile: 'score < 3,5',
    'En cours': 'de 3,5 à 4,99, ou sans résultat',
    Acquis: 'score ≥ 5',
  },
  prerequis: {
    Fragile: 'au moins un prérequis à renforcer',
    'En cours': 'aucun à renforcer et au moins un en cours, ou sans résultat',
    Acquis: 'prérequis mesurés tous maîtrisés',
  },
};

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400';

/**
 * Les 3 groupes de besoin d'une session `groupes` (F5, R7, R8, R9). Composant
 * de présentation : l'état vit dans `QCMDashboard`.
 * - Un élève se déplace par glisser-déposer, ou au clavier : on le choisit
 *   (sa pastille), puis « Déplacer … ici » dans la colonne voulue.
 * - Les exercices (le commun et ceux de chaque groupe) se gèrent dans le
 *   panier (R9 amendée le 2026-10-03) ; la colonne n'en donne que le nombre.
 * @param {{ groupes: Array<{ nom, eleves, sansResultat, coupsDePouce, propres: Array, sansComplexe? }>,
 *   base: 'attendu'|'prerequis', nbCommuns: number, nomEleve: (id) => string,
 *   onDeplacer, onBasculerAides }} props
 */
const GroupesBesoin = ({ groupes, base, nbCommuns, nomEleve, onDeplacer, onBasculerAides }) => {
  const [choisi, setChoisi] = useState(null);
  // Après un déplacement : l'annonce (lecteurs d'écran) et la pastille où
  // rendre le focus, le bouton « Déplacer » cliqué venant de disparaître.
  const [annonce, setAnnonce] = useState('');
  const [aFocaliser, setAFocaliser] = useState(null);
  const pastilles = useRef(new Map());
  const consigneId = useId();
  const groupeDe = (id) => groupes.find((g) => g.eleves.includes(id))?.nom;

  useEffect(() => {
    if (aFocaliser == null) return;
    pastilles.current.get(aFocaliser)?.focus();
    setAFocaliser(null);
  }, [aFocaliser, groupes]);

  const deplacer = (id, vers, { rendreFocus = false } = {}) => {
    const depart = groupeDe(id);
    if (!depart || depart === vers) return;
    onDeplacer(id, vers);
    setChoisi(null);
    const nb = (groupes.find((g) => g.nom === vers)?.eleves.length || 0) + 1;
    setAnnonce(`${nomEleve(id)} déplacé vers ${vers}, ${pluriel(nb, 'élève')}.`);
    if (rendreFocus) setAFocaliser(id);
  };

  return (
    <div className="grid gap-3 md:grid-cols-3">
      <p id={consigneId} className="sr-only">Choisissez un élève, puis « Déplacer vers » le groupe voulu, ou glissez-le dans ce groupe.</p>
      <div aria-live="polite" className="sr-only">{annonce}</div>
      {groupes.map((g) => {
        const sansResultat = new Set(g.sansResultat);
        return (
          // div et non section : index.css centre toute <section> en flex.
          <div
            key={g.nom}
            role="region"
            aria-label={`Groupe ${g.nom}`}
            className="rounded-xl border border-gray-200 bg-white p-3 text-sm flex flex-col gap-2"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); deplacer(Number(e.dataTransfer.getData('text/plain')), g.nom); }}
          >
            <header>
              <h3 className="font-bold text-gray-800">{g.nom}</h3>
              <p className="text-[11px] text-gray-500">{CRITERES[base][g.nom]}</p>
            </header>

            <p className="text-xs font-semibold text-gray-700">{pluriel(g.eleves.length, 'élève')}</p>
            <ul className="flex flex-wrap gap-1" aria-label={`Élèves du groupe ${g.nom}`}>
              {g.eleves.map((id) => {
                const nom = nomEleve(id);
                const marque = sansResultat.has(id);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      ref={(el) => { if (el) pastilles.current.set(id, el); else pastilles.current.delete(id); }}
                      aria-describedby={consigneId}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('text/plain', String(id))}
                      onClick={() => setChoisi((c) => (c === id ? null : id))}
                      aria-pressed={choisi === id}
                      aria-label={`${nom}${marque ? ', sans résultat' : ''}`}
                      className={`px-2 py-0.5 rounded-full text-[11px] border ${FOCUS} ${
                        choisi === id ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-700 border-gray-200'
                      }`}
                    >
                      {nom}
                      {marque && <span className="ml-1 italic opacity-70">sans résultat</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
            {choisi != null && groupeDe(choisi) !== g.nom && (
              <button type="button" onClick={() => deplacer(choisi, g.nom, { rendreFocus: true })} className={`self-start text-xs font-semibold text-blue-700 hover:underline rounded ${FOCUS}`}>
                Déplacer {nomEleve(choisi)} vers {g.nom}
              </button>
            )}

            <button
              type="button"
              aria-pressed={g.coupsDePouce}
              aria-label={`Coups de pouce autorisés, groupe ${g.nom}`}
              onClick={() => onBasculerAides(g.nom)}
              className={`self-start px-2 py-1 rounded-lg text-xs border ${FOCUS} ${
                g.coupsDePouce ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-gray-50 border-gray-200 text-gray-600'
              }`}
            >
              Coups de pouce : {g.coupsDePouce ? 'autorisés' : 'non'}
            </button>

            <p className="text-[11px] text-gray-500">
              {pluriel(nbCommuns + g.propres.length, 'exercice')}, dont {pluriel(g.propres.length, 'propre')} au groupe · dans le panier
            </p>
            {g.sansComplexe && <p className="text-[11px] text-gray-500">Aucun exercice complexe repéré pour cet attendu.</p>}
          </div>
        );
      })}
    </div>
  );
};

export default GroupesBesoin;
